import React from "react";
import Carousel from "react-bootstrap/Carousel";

import { ProposalLayout } from "../../types/proposalDocuments.types";

import { useProposalDocument } from "./proposalDocumentsContext";
import LayoutPreview from "./quotes/layoutPreview";

interface LayoutOption {
  value: ProposalLayout;
  label: string;
  hint: string;
}

const LAYOUT_OPTIONS: LayoutOption[] = [
  {
    value: "supplier",
    label: "Supplier",
    hint: "One section per supplier.",
  },
  {
    value: "comparison",
    label: "Comparison",
    hint: "Lay quotes out for side-by-side comparison.",
  },
];

const ThemePicker: React.FC = () => {
  const { previewsLoading, payload, setPayload, themePreviews } = useProposalDocument();

  const handleThemeSelect = (themeName: string) => {
    setPayload({ ...payload, theme: themeName });
  };

  const handleLayoutChange = (layout: ProposalLayout) => {
    setPayload({ ...payload, layout });
  };

  return (
    <div className="theme-picker">
      <div className="theme-picker__header">
        <h2>Theme &amp; Layout</h2>
      </div>

      <div className="theme-picker__content">
        {/* Document Layout */}
        <div className="theme-picker__layout-section">
          <h3 className="theme-picker__layout-heading">Document Layout</h3>
          <div className="theme-picker__layout-grid">
            {LAYOUT_OPTIONS.map((option) => (
              <label
                key={option.value}
                className={`layout-option-card ${
                  payload.layout === option.value ? "layout-option-card--selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="proposalLayout"
                  value={option.value}
                  checked={payload.layout === option.value}
                  onChange={() => {
                    handleLayoutChange(option.value);
                  }}
                  className="layout-option-card__radio-input"
                  aria-label={`${option.label} layout`}
                />
                <div className="layout-option-card__preview">
                  <LayoutPreview layout={option.value} />
                </div>
                <div className="layout-option-card__footer">
                  <span className="layout-option-card__label">{option.label}</span>
                  <span className="layout-option-card__hint">{option.hint}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <h3 className="theme-picker__layout-heading">Theme</h3>

        {previewsLoading ? (
          <div className="theme-picker__loading">
            <div className="spinner" aria-hidden="true" />
            <span className="loading-text">Loading theme previews...</span>
          </div>
        ) : (
          <div className="theme-picker__grid">
            {Object.entries(themePreviews).map(([themeName, preview]) => {
              const images = [
                { url: preview.cover_image, caption: "Front Cover" },
                ...preview.content_images.flatMap((s) => [
                  { url: s.left, caption: "Page Spread (Left)" },
                  { url: s.right, caption: "Page Spread (Right)" },
                ]),
                {
                  url: preview.sla_content_image.left,
                  caption: "SLA Spread (Left)",
                },
                {
                  url: preview.sla_content_image.right,
                  caption: "SLA Spread (Right)",
                },
                { url: preview.sla_hero_image, caption: "SLA Hero" },
              ];
              const isSelected = payload.theme === themeName;

              return (
                <div
                  key={themeName}
                  className={`theme-card ${isSelected ? "theme-card--selected" : ""}`}
                  onClick={() => {
                    handleThemeSelect(themeName);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleThemeSelect(themeName);
                    }
                  }}
                  aria-pressed={isSelected}
                  aria-label={`${themeName} theme${isSelected ? ", selected" : ""}`}
                >
                  <div className="theme-card__carousel">
                    <Carousel
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {images.map((image, i) => (
                        <Carousel.Item key={i}>
                          <img
                            src={image.url}
                            className="d-block w-100"
                            alt={`${themeName} - ${image.caption}`}
                          />
                          <Carousel.Caption>
                            <span className="badge bg-primary">{image.caption}</span>
                          </Carousel.Caption>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </div>

                  <div className="theme-card__footer">
                    <h3 className="theme-card__title">{themeName}</h3>
                    {isSelected && <span className="theme-card__badge">Selected</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThemePicker;
